import { getSupabaseClient } from '../supabase';
import type { Subscription, PlanTier } from '@traction/shared';
import { PLAN_CONFIGS } from '@traction/shared';

// Supabase functions URL
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

interface BillingPortalResponse {
  url: string;
}

/**
 * Create a Stripe checkout session for subscription
 */
export async function createCheckoutSession(
  userId: string,
  priceId: string,
  successUrl?: string,
  cancelUrl?: string
): Promise<CheckoutSessionResponse> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      user_id: userId,
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create checkout session');
  }

  return response.json();
}

/**
 * Create a Stripe billing portal session for subscription management
 */
export async function createBillingPortalSession(
  userId: string,
  returnUrl?: string
): Promise<BillingPortalResponse> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/create-billing-portal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      user_id: userId,
      return_url: returnUrl,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create billing portal session');
  }

  return response.json();
}

export async function getSubscription(userId: string): Promise<Subscription | null> {
  const { data, error } = await getSupabaseClient()
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data as Subscription;
}

export async function createTrialSubscription(
  userId: string,
  stripeCustomerId: string
): Promise<Subscription> {
  const now = new Date();
  const trialEnd = new Date(now);
  trialEnd.setDate(trialEnd.getDate() + 14);

  const { data, error } = await getSupabaseClient()
    .from('subscriptions')
    .insert({
      user_id: userId,
      stripe_customer_id: stripeCustomerId,
      plan_tier: 'trial',
      status: 'trialing',
      current_period_start: now.toISOString(),
      current_period_end: trialEnd.toISOString(),
      trial_ends_at: trialEnd.toISOString(),
      cancel_at_period_end: false,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Subscription;
}

export async function isSubscriptionActive(userId: string): Promise<boolean> {
  const subscription = await getSubscription(userId);

  if (!subscription) return false;

  const validStatuses = ['trialing', 'active'];
  if (!validStatuses.includes(subscription.status)) return false;

  const now = new Date();
  const periodEnd = new Date(subscription.current_period_end);

  return now < periodEnd;
}

export function getLeadsPerDay(tier: PlanTier): number {
  return PLAN_CONFIGS[tier]?.leads_per_day ?? 3;
}

// === Lifecycle Emails (S4-9, S4-10, S4-11) ===

type EmailTemplate = 'welcome' | 'verification' | 'receipt' | 'daily_briefing' | 'warm_reply' | 'password_reset';

interface SendEmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Send a templated email
 */
export async function sendEmail(
  to: string | string[],
  template: EmailTemplate,
  data?: Record<string, any>
): Promise<SendEmailResult> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ to, template, data }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, error: result.error || 'Failed to send email' };
  }

  return { success: true, messageId: result.messageId };
}

/**
 * Send welcome email to new user (S4-9)
 */
export async function sendWelcomeEmail(
  email: string,
  name?: string
): Promise<SendEmailResult> {
  return sendEmail(email, 'welcome', {
    name,
    dashboardUrl: `${window.location.origin}/dashboard`,
  });
}

/**
 * Send payment receipt email (S4-9)
 */
export async function sendReceiptEmail(
  email: string,
  data: {
    name?: string;
    planName: string;
    amount: string;
    billingPeriod: string;
  }
): Promise<SendEmailResult> {
  return sendEmail(email, 'receipt', {
    ...data,
    date: new Date().toLocaleDateString(),
    billingUrl: `${window.location.origin}/dashboard/subscription`,
  });
}

/**
 * Send warm reply alert email (S4-11)
 */
export async function sendWarmReplyAlert(
  email: string,
  data: {
    userName?: string;
    leadName: string;
    companyName: string;
    intent?: string;
    suggestedAction?: string;
    replyPreview?: string;
    leadId?: string;
  }
): Promise<SendEmailResult> {
  return sendEmail(email, 'warm_reply', {
    ...data,
    leadUrl: data.leadId
      ? `${window.location.origin}/dashboard/leads?lead=${data.leadId}`
      : `${window.location.origin}/dashboard/leads`,
  });
}

/**
 * Trigger daily briefing for a user (S4-10)
 */
export async function triggerDailyBriefing(userId: string): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/send-daily-briefing`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ user_id: userId }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { success: false, error: result.error || 'Failed to trigger daily briefing' };
  }

  return { success: true };
}
